import React from 'react';
import { X, Plus } from 'lucide-react';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FeatureLimitHint } from '../../../components/FeatureLimitHint';
import { useFeatureLimits } from '../../../hooks/useFeatureLimits';
import { SortablePipelinePhaseRow } from './SortablePipelinePhaseRow';
import type { LeadPipeline } from '../../../types';

const inputClass =
  'px-3 py-2 border border-app-border rounded-lg text-sm focus:ring-1 focus:ring-pipedrive-blue outline-none bg-app-surface';

type StageDraft = { id?: string; label: string };

interface LeadPipelineQuickEditModalProps {
  open: boolean;
  onClose: () => void;
  pipeline: LeadPipeline | null;
  onSaved: (pipeline: LeadPipeline) => void;
}

export function LeadPipelineQuickEditModal({
  open,
  onClose,
  pipeline,
  onSaved,
}: LeadPipelineQuickEditModalProps) {
  const [stages, setStages] = React.useState<StageDraft[]>([]);
  const [draft, setDraft] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { usage, loaded: limitsReady } = useFeatureLimits();
  const phaseLimit = usage('pipelinePhases');
  const phasesAtLimit = stages.length >= phaseLimit.max;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  React.useEffect(() => {
    if (open && pipeline) {
      setStages(pipeline.stages.map((s) => ({ id: s.id, label: s.label })));
      setDraft('');
      setError(null);
    }
  }, [open, pipeline]);

  const handleDragEnd = React.useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = Number.parseInt(String(active.id), 10);
    const newIndex = Number.parseInt(String(over.id), 10);
    if (!Number.isFinite(oldIndex) || !Number.isFinite(newIndex)) return;
    setStages((list) => arrayMove(list, oldIndex, newIndex));
  }, []);

  const handleSave = async () => {
    if (!pipeline) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/settings/lead-pipelines/${encodeURIComponent(pipeline.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: pipeline.name,
          stages: stages.map((s) => ({ id: s.id, label: s.label })),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as LeadPipeline & { error?: string };
      if (!res.ok) {
        setError(data?.error || `Speichern fehlgeschlagen (${res.status})`);
        return;
      }
      onSaved(data);
      onClose();
    } catch {
      setError('Netzwerkfehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !pipeline) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-pipeline-quick-edit-title"
        className="bg-app-surface rounded-xl shadow-xl border border-app-border w-full max-w-lg max-h-[min(90vh,640px)] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 shrink-0 px-5 pt-4 pb-2">
          <div className="min-w-0">
            <h2 id="lead-pipeline-quick-edit-title" className="text-base font-bold text-app-text">
              {pipeline.name}
            </h2>
            <p className="text-xs text-app-text-subtle mt-1">
              Phasen bearbeiten und sortieren. „Gewonnen“ und „Verloren“ bleiben fest und erscheinen beim Ziehen unten.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !saving && onClose()}
            className="p-2 rounded-lg text-slate-400 hover:bg-app-hover hover:text-app-text-muted shrink-0"
            aria-label="Schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-5 pt-1 pb-4">
          {error ? (
            <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
          ) : null}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={stages.map((_, i) => String(i))} strategy={verticalListSortingStrategy}>
              <ul className="space-y-0">
                {stages.map((stage, index) => (
                  <SortablePipelinePhaseRow
                    key={stage.id ?? `new-${index}`}
                    id={String(index)}
                    index={index}
                    phase={stage.label}
                    inputClassName={inputClass}
                    canRemove={stages.length > 1}
                    onChange={(i, v) =>
                      setStages((list) => list.map((p, j) => (j === i ? { ...p, label: v } : p)))
                    }
                    onRemove={(i) =>
                      setStages((list) => {
                        if (list.length <= 1) return list;
                        return list.filter((_, j) => j !== i);
                      })
                    }
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={120}
              placeholder="Neue Phase"
              className={`flex-1 ${inputClass}`}
              disabled={phasesAtLimit}
            />
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium border border-app-border rounded-lg text-app-text-muted hover:bg-app-hover disabled:opacity-40 shrink-0"
              disabled={phasesAtLimit}
              onClick={() => {
                const t = draft.trim();
                if (!t) return;
                setStages((list) => [...list, { label: t }]);
                setDraft('');
              }}
            >
              <Plus className="w-4 h-4 inline-block mr-1 align-text-bottom" />
              Hinzufügen
            </button>
          </div>
          <FeatureLimitHint
            className="mt-2"
            limitKey="pipelinePhases"
            used={stages.length}
            max={phaseLimit.max}
            ready={limitsReady}
          />
        </div>

        <div className="flex shrink-0 justify-end gap-2 bg-app-surface-muted/80 px-5 py-4">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-app-text-muted hover:bg-slate-200/80 rounded-lg"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSave()}
            className="px-4 py-2 text-sm font-medium text-white bg-pipedrive-blue rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
